import type { CSSProperties } from "react";
import { useLocale } from "@genius-tracker/i18n/react";
import "./BrandLogo.css";

/**
 * The approved Genius Tracker brand logo (Owner, 2026-10-05), shown as the original
 * raster artwork from `public/brand/` instead of the earlier SVG redraw.
 *
 * Both themes use approved transparent artwork. The dark files now carry the
 * same transparent pixels as the light files, without the former navy rectangle.
 * Both are rendered and CSS hides the one that does not match, so the logo follows
 * the theme without a React theme dependency (`PublicAuthShell` renders it outside the shell).
 *
 * - `wordmark` (default): arrow + GENIUS TRACKER, aspect 720:180.
 * - `mark`: arrow only, square — the collapsed sidebar.
 *
 * The accessible name is always the app title; the hidden artwork is removed from
 * the accessibility tree by `display: none`.
 */
export type BrandLogoVariant = "wordmark" | "mark";

const ARTWORK: Record<BrandLogoVariant, { readonly light: string; readonly dark: string }> = {
  wordmark: {
    light: "/brand/genius-tracker-navbar-on-light.png",
    dark: "/brand/genius-tracker-navbar-on-dark.png",
  },
  mark: {
    light: "/brand/gt-logo-mark-light.png",
    dark: "/brand/gt-logo-mark-dark.png",
  },
};

export function BrandLogo({
  height = 32,
  variant = "wordmark",
}: {
  readonly height?: number;
  readonly variant?: BrandLogoVariant;
}) {
  const { t } = useLocale();
  const label = t("app.title");
  const artwork = ARTWORK[variant];
  const width = variant === "mark" ? height : Math.round((height * 720) / 180);

  return (
    <span data-testid="app-brand-logo" style={brandWrapStyle}>
      <img
        className="gt-brand-logo gt-brand-logo--light"
        data-testid="app-brand-logo-light"
        src={artwork.light}
        alt={label}
        width={width}
        height={height}
        decoding="async"
        style={{ ...artworkStyle, inlineSize: width, blockSize: height }}
      />
      <img
        className="gt-brand-logo gt-brand-logo--dark"
        data-testid="app-brand-logo-dark"
        src={artwork.dark}
        alt={label}
        width={width}
        height={height}
        decoding="async"
        style={{ ...artworkStyle, inlineSize: width, blockSize: height }}
      />
    </span>
  );
}

/* Explicit box: the artwork is scaled to `height` (and `width`) and can never spill past the
 * sidebar brand tile, whatever the image's intrinsic size is. */
const artworkStyle: CSSProperties = {
  maxInlineSize: "100%",
  objectFit: "contain",
};

const brandWrapStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  lineHeight: 0,
};
